import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Supply48DataSource } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'

import type { Supply48CsvExportRow } from '~/models/supply48DataCsv'
import { sourcePublicTradeId } from '~/pages/Supply48Data/models/scrollTarget'

/**
 * 48点データ画面からの CSV エクスポート (spec `supply/supply-48-data.md` AC-S4D-007a)。
 *
 * ## なぜ画面の state から作るのか
 *
 * AC-S4D-007a は「本画面の現在のフィルタ状態を反映した CSV」 を求めている。
 * 全件エクスポート RPC (`ExportSupply48Data`) は撤去済みで、 画面のフィルタを渡せる
 * 経路は他にない。
 *
 * 画面が表示している `ListSupply48Data` の応答 (`data.sources`) は、 売買 / 区分 /
 * エリア / 対象日で **backend が既に絞り込んだ集合**そのものなので、 これを writer に
 * 流せば AC を満たせる。
 *
 * ## 保存済みの値だけを出す
 *
 * AC-S4D-007a は「ダウンロード対象は本画面の**保存済み**データ (DB 上の確定値) であり、
 * 未保存の編集は含まれない」 と定める。 ここで読むのは応答 (= サーバ値) の
 * `day.slots` だけで、 画面の編集 state (`editState`) は**参照しない**。
 * 未保存の編集がある旨の告知は呼び出し側 (確認モーダル) の責務。
 *
 * ## 列構成
 *
 * 出力は取込パーサと同じ 51 列 (取引ID + 日付 + コマ1〜48 + delete、
 * `docs/specs/admin/data/uploads/supply-48-data.md` §1、D10)。 delete 列は
 * 常に空 (画面エクスポートは削除フラグを持たない)。 本関数は `Supply48CsvExportRow`
 * (publicTradeId / deliveryDate / slots) を組み立てるだけで列数を持たないため、
 * `buildSupply48DataCsv` 側の列定義が変わればそのまま追随する。
 */

/**
 * 画面が表示中の供給源から CSV エクスポート行を組み立てる (F1 純関数)。
 *
 * - 取引IDを持たない供給源 (市場 / 発電) は除外する。 CSV の主キーが取引IDで、
 *   取込側も取引IDで対象を解決するため、 出しても再取込できない
 * - 日付は CSV 列仕様の "YYYY-MM-DD" に正規化する
 * - 値未設定のコマは null (= CSV 上の空セル。 取込時は「変更しない」 扱い)
 * - `slots` が 48 要素に満たない応答でも 48 要素に揃える (列ズレを起こさない側に倒す。
 *   proto の `min_items=48` はリクエスト側の検証で、 応答は wire 上そのまま届きうる)
 * - 並びは 供給源の表示順 → その中で受渡日の昇順
 */
export function toScreenCsvExportRows(
  sources: readonly Supply48DataSource[],
): Supply48CsvExportRow[] {
  const rows: Supply48CsvExportRow[] = []
  for (const source of sources) {
    const publicTradeId = sourcePublicTradeId(source)
    if (publicTradeId === undefined) continue
    const days = [...source.days].sort((a, b) => {
      const da = a.deliveryDate
      const db = b.deliveryDate
      if (da === undefined || db === undefined) return 0
      return da.year - db.year || da.month - db.month || da.day - db.day
    })
    for (const day of days) {
      const date = day.deliveryDate
      if (date === undefined) continue
      rows.push({
        publicTradeId,
        deliveryDate: `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`,
        slots: Array.from(
          { length: 48 },
          (_, komaIdx) => day.slots[komaIdx]?.value ?? null,
        ),
      })
    }
  }
  return rows
}

/**
 * 出力対象の基準時点 (AC-S4D-007「基準時点 (最終更新時刻) を表示する」)。
 *
 * 画面ヘッダーに 最終更新 の表示が無いので、 **出力する行の中で最も新しい
 * `updatedAt`** を基準時点として使う (= このファイルが「いつ時点の値か」 を
 * そのまま表す)。 1 件も無ければ undefined (呼び出し側で「—」)。
 */
export function latestUpdatedAt(
  sources: readonly Supply48DataSource[],
): Date | undefined {
  let latest: Date | undefined
  for (const source of sources) {
    // **CSV に出る行と同じ母集合だけを見る。** 取引IDを持たない供給源 (市場 / 発電)
    // と受渡日を持たない day は [[toScreenCsvExportRows]] が除外するので、
    // ここで数えると「ファイルに入っていない行の更新時刻」 が基準時点になり、
    // 出力ファイルより新しい時刻を表示してしまう。
    if (sourcePublicTradeId(source) === undefined) continue
    for (const day of source.days) {
      if (day.deliveryDate === undefined) continue
      const ts = day.updatedAt
      if (ts === undefined) continue
      const at = timestampDate(ts)
      if (latest === undefined || at > latest) latest = at
    }
  }
  return latest
}

/**
 * ダウンロードファイル名 (AC-S4D-007a)。
 *
 * `48点データ_{開始日}-{終了日}.csv` (英語表示時は `48-data_...`)。
 * 単日でも開始日 = 終了日で 2 回書く (spec の例 `48点データ_20260901-20260901.csv`)。
 *
 * 他のデータ種別が**ダウンロード実行日**を使うのに対し、 48点データだけ
 * **出力した対象期間**を使う (= 同 AC が明示する 48点データ限定の例外。
 * 出力範囲をファイル名から判別できることを優先する)。
 */
export function screenCsvFileName({
  from,
  to,
  isEnglish,
}: {
  from: Date
  to: Date
  isEnglish: boolean
}): string {
  const ymd = (d: Date): string =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  const base = isEnglish ? '48-data' : '48点データ'
  return `${base}_${ymd(from)}-${ymd(to)}.csv`
}
